/**
 * Real `ReceiveHostDeps`/`ReceiveResumeDeps` wiring + host bookkeeping for
 * the web app's receive flow — mirrors
 * `apps/extension/src/offscreen/index.ts` + `offscreen/filetransfer/receiveDeps.ext.ts`
 * + `receiveResumeDeps.ext.ts` combined into ONE module. The extension needs
 * that three-way split because its receive loop is hosted by a SEPARATE
 * offscreen document the SW creates/idle-closes; the web app has no such
 * split — the page IS the only context, so there's no idle-close scheduler
 * and no cross-context message relay, just a plain in-memory
 * `activeReceives` map + a subscriber list the React hook drains.
 *
 * `deps.p2p` is OMITTED entirely — see senderTransfer.web.ts's module doc
 * for the rationale (P2P is out of scope for M4 web).
 *
 * SECURITY: never log file name/content/manifest fields — security.md.
 */

import {
  createReceiverWorkerClient,
  detectOpfsStorage,
  type FtReceiverStateRecord,
  isActiveTransferStatus,
  ReceiveAbortedError,
  type ReceiveHostDeps,
  type ReceiveResumeDeps,
  type ReceiveResumeOutput,
  type ResumeTerminalOutcome,
  receiveFile,
  resumePendingReceives,
  resumeReceiverTransfer,
} from "@reflecto/client-core";
import {
  CHUNKS_PER_PART,
  decodeBase64,
  decryptChunk,
  deriveSharedSecret,
  deriveTransferKey,
  type FileCtrl,
  sha256Final,
  sha256Init,
  sha256Update,
  type TransferManifest,
} from "@reflecto/core";
import { webStorage } from "../adapters/storage.web";
import {
  deleteTransfer,
  fetchTransferPartStream,
  getTransfer,
  getTransferDownloadUrl,
} from "../api/client";
import { logError, logWarn } from "../lib/log";
import { webDownloads } from "./downloads.web";
import { ftErrorIdentity } from "./errorIdentity";
import { sendFileCtrl } from "./fileCtrl";
import { reportReceiveTerminal } from "./ftAnalytics";
import { createWebOpfsWorker } from "./opfsWorker.web";
import {
  getReceiverState,
  listReceiverStates,
  pruneReceiverStates,
  removeReceiverState,
  saveReceiverState,
} from "./receiverState";
import { listSenderStates } from "./senderState";

export type ReceiveTransferEvent =
  | {
      type: "offer";
      transferId: string;
      fileName: string;
      fileSize: number;
      senderDeviceId: string;
    }
  | { type: "offerCancelled"; transferId: string }
  | { type: "progress"; transferId: string; partIndex: number; partCount: number }
  | {
      type: "done";
      transferId: string;
      outcome: "complete" | "failed" | "cancelled";
      errorCode?: string;
    };

const listeners = new Set<(event: ReceiveTransferEvent) => void>();

/** Subscribe to receive-side events (offer/progress/done). Returns an unsubscribe function. */
export function subscribeReceiveTransferEvents(
  listener: (event: ReceiveTransferEvent) => void
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(event: ReceiveTransferEvent): void {
  for (const listener of listeners) listener(event);
}

interface ActiveReceive {
  aborted: boolean;
}

const activeReceives = new Map<string, ActiveReceive>();

/** Web Locks single-writer guard (§3.1.2) — mirrors senderTransfer.web.ts's bridge. */
const heldLockReleasers = new Map<string, () => void>();

async function acquireLock(transferId: string): Promise<boolean> {
  if (heldLockReleasers.has(transferId)) return false;
  return new Promise<boolean>((resolveAcquire) => {
    let resolveRelease: () => void = () => {};
    const released = new Promise<void>((r) => {
      resolveRelease = r;
    });
    navigator.locks.request(`reflecto-ft-${transferId}`, { ifAvailable: true }, async (lock) => {
      if (!lock) {
        resolveAcquire(false);
        return;
      }
      heldLockReleasers.set(transferId, resolveRelease);
      resolveAcquire(true);
      await released;
    });
  });
}

async function releaseLock(transferId: string): Promise<void> {
  const release = heldLockReleasers.get(transferId);
  if (!release) return;
  heldLockReleasers.delete(transferId);
  release();
}

/**
 * Fresh-receive persistence hooks: hang `FtReceiverStateRecord` writes off
 * `receiveHost.ts`'s P-R1/P-R2/P-R4 seams so a fresh (non-resumed)
 * receive leaves a `"transferring"` record a later resume scan can find.
 */
function createFreshReceivePersistence(manifest: TransferManifest, senderDeviceId: string) {
  const transferId = manifest.transferId;
  const file = manifest.files[0];
  const partCount = Math.ceil(file.chunkCount / CHUNKS_PER_PART);
  let record: FtReceiverStateRecord | null = null;

  return {
    async onOutputAllocated(): Promise<void> {
      const { phonePubKey } = await webStorage.get("phonePubKey");
      const now = Date.now();
      record = {
        v: 1,
        transferId,
        peerDeviceId: senderDeviceId,
        peerPub: phonePubKey ?? "",
        manifestJson: JSON.stringify(manifest),
        partCount,
        partsDownloaded: [],
        chunksVerified: 0,
        status: "transferring",
        acceptedAt: now,
        expiresAt: 0,
        createdAt: now,
        updatedAt: now,
        opfsPath: `ft/recv-${transferId}`,
      };
      await saveReceiverState(record).catch(() => {});
    },
    async onPartWritten(_partIndex: number, chunksVerified: number): Promise<void> {
      if (!record) return;
      record.chunksVerified = chunksVerified;
      record.partsDownloaded = Array.from(
        { length: Math.ceil(chunksVerified / CHUNKS_PER_PART) },
        (_v, i) => i
      );
      record.updatedAt = Date.now();
      await saveReceiverState(record).catch(() => {});
    },
    async onTerminal(
      status: "complete" | "failed" | "cancelled",
      errorCode?: string
    ): Promise<void> {
      if (!record) return;
      record.status = status;
      record.errorCode = errorCode;
      record.updatedAt = Date.now();
      await saveReceiverState(record).catch(() => {});
    },
  };
}

/** Build the `ReceiveResumeOutput` seam for `transferId` (worker client + async-API existence probe). */
function openOutput(transferId: string): ReceiveResumeOutput {
  const worker = createReceiverWorkerClient(transferId, {
    createWorker: createWebOpfsWorker,
    storage: detectOpfsStorage(),
  });
  const fileName = `recv-${transferId}`;
  return {
    probeSize: async () => {
      try {
        const root = await navigator.storage.getDirectory();
        const dir = await root.getDirectoryHandle("ft", { create: false });
        const handle = await dir.getFileHandle(fileName, { create: false });
        const file = await handle.getFile();
        return file.size;
      } catch {
        return null;
      }
    },
    open: () => worker.open(),
    truncate: (size) => worker.truncate(size),
    readRange: (start, end) => worker.readRange(start, end),
    writeAt: (offset, data) => worker.writeAt(offset, data),
    flush: () => worker.flush(),
    finalize: () => worker.finalize(),
    deleteCopy: () => worker.deleteCopy(),
    dispose: () => worker.terminate(),
  };
}

/** Build a real `ReceiveHostDeps` for receiving `transferId` from `senderDeviceId`. Throws `"not_paired"` if the app has no stored keys/token yet. */
async function createWebReceiverDeps(
  transferId: string,
  senderDeviceId: string,
  hooks: {
    onOutputAllocated?(): Promise<void>;
    onPartWritten?(partIndex: number, chunksVerified: number): Promise<void>;
    onTerminal?(status: "complete" | "failed" | "cancelled", errorCode?: string): Promise<void>;
    onProgress?(partIndex: number, partCount: number): void;
    isAborted?(): boolean;
  }
): Promise<{ deps: ReceiveHostDeps; dispose(): void }> {
  const { token, secretKey, publicKey, phonePubKey } = await webStorage.get(
    "token",
    "secretKey",
    "publicKey",
    "phonePubKey"
  );
  if (!token || !secretKey || !publicKey || !phonePubKey) {
    throw new Error("not_paired");
  }
  const secretKeyBytes = decodeBase64(secretKey);
  const ownPublicKey = decodeBase64(publicKey);
  const senderPublicKey = decodeBase64(phonePubKey);
  const sharedSecret = await deriveSharedSecret(secretKeyBytes, senderPublicKey);

  const workerClient = createReceiverWorkerClient(transferId, {
    createWorker: createWebOpfsWorker,
    storage: detectOpfsStorage(),
  });

  const deps: ReceiveHostDeps = {
    api: {
      getTransfer: (tid) => getTransfer(token, tid),
      getDownloadUrl: async (tid, partIndex) => {
        const { url } = await getTransferDownloadUrl(token, tid, partIndex);
        return { url };
      },
      fetchPartStream: (url) => fetchTransferPartStream(url),
    },
    output: workerClient,
    crypto: { deriveTransferKey, decryptChunk },
    hash: { init: sha256Init, update: sha256Update, final: sha256Final },
    downloads: webDownloads,
    sendCtrl: (ctrl) => sendFileCtrl(senderDeviceId, ctrl),
    sharedSecret,
    senderPublicKey,
    ownPublicKey,
    onOutputAllocated: hooks.onOutputAllocated,
    onPartWritten: hooks.onPartWritten,
    onTerminal: hooks.onTerminal,
    onProgress: hooks.onProgress,
    isAborted: hooks.isAborted,
    // p2p intentionally omitted — see module doc.
  };
  return { deps, dispose: () => workerClient.terminate() };
}

/** Build a real `ReceiveResumeDeps`. Throws `"not_paired"` if the app has no stored keys/token yet. */
async function createWebReceiveResumeDeps(): Promise<ReceiveResumeDeps> {
  const { token, secretKey, publicKey } = await webStorage.get("token", "secretKey", "publicKey");
  if (!token || !secretKey || !publicKey) {
    throw new Error("not_paired");
  }
  const secretKeyBytes = decodeBase64(secretKey);
  const ownPublicKey = decodeBase64(publicKey);

  return {
    listReceiverStates,
    saveReceiverState,
    removeReceiverState,
    acquireLock,
    releaseLock,
    api: {
      getTransfer: (transferId) => getTransfer(token, transferId),
      getDownloadUrl: async (transferId, partIndex) => {
        const { url } = await getTransferDownloadUrl(token, transferId, partIndex);
        return { url };
      },
      fetchPartStream: (url) => fetchTransferPartStream(url),
      deleteTransfer: (transferId) => deleteTransfer(token, transferId),
    },
    openOutput,
    crypto: { deriveTransferKey, decryptChunk },
    hash: { init: sha256Init, update: sha256Update, final: sha256Final },
    downloads: webDownloads,
    sendCtrl: sendFileCtrl,
    deriveSharedSecret: (senderPub) => deriveSharedSecret(secretKeyBytes, senderPub),
    ownPublicKey,
    beginTransfer: (transferId) => {
      if (activeReceives.has(transferId)) return false;
      activeReceives.set(transferId, { aborted: false });
      return true;
    },
    endTransfer: (transferId) => {
      activeReceives.delete(transferId);
    },
    isLoopAborted: (transferId) => activeReceives.get(transferId)?.aborted ?? false,
    isAborted: () => false,
    onProgress: (transferId, partIndex, partCount) => {
      emit({ type: "progress", transferId, partIndex, partCount });
    },
    onTerminal: (transferId, outcome: ResumeTerminalOutcome, errorCode) => {
      const doneOutcome = outcome === "expired" ? "failed" : outcome;
      const resolvedCode = errorCode ?? (outcome === "expired" ? "EXPIRED" : undefined);
      // The resume driver's terminal — resumed=true. Expired is a real failure with a code, so it
      // must not be reported as a bare failure with no error_code.
      reportReceiveTerminal(doneOutcome, resolvedCode, true);
      emit({ type: "done", transferId, outcome: doneOutcome, errorCode: resolvedCode });
    },
  };
}

/** Start (or ignore a duplicate) receive loop for an accepted offer. */
export function startReceive(
  transferId: string,
  senderDeviceId: string,
  manifest: TransferManifest
): void {
  if (activeReceives.has(transferId)) return;
  const state: ActiveReceive = { aborted: false };
  activeReceives.set(transferId, state);
  const partCount = Math.ceil(manifest.files[0].chunkCount / CHUNKS_PER_PART);

  function finish(outcome: "complete" | "failed" | "cancelled", errorCode?: string): void {
    if (!activeReceives.delete(transferId)) return;
    // Inside the once-guard: the single fresh-receive terminal. resumed=false — this path only
    // ever runs for a receive started in THIS page session (#808 web surface).
    reportReceiveTerminal(outcome, errorCode, false);
    emit({ type: "done", transferId, outcome, errorCode });
  }

  const persistence = createFreshReceivePersistence(manifest, senderDeviceId);

  void (async () => {
    let dispose: (() => void) | undefined;
    try {
      const receiverDeps = await createWebReceiverDeps(transferId, senderDeviceId, {
        onOutputAllocated: persistence.onOutputAllocated,
        onPartWritten: persistence.onPartWritten,
        onProgress: (partIndex) => {
          emit({ type: "progress", transferId, partIndex, partCount });
        },
        onTerminal: async (status, errorCode) => {
          await persistence.onTerminal(status, errorCode);
          finish(status, errorCode);
        },
        isAborted: () => state.aborted,
      });
      dispose = receiverDeps.dispose;
      await receiveFile(manifest, receiverDeps.deps);
    } catch (err) {
      if (err instanceof ReceiveAbortedError) {
        // Cancel/unpair — NOT a failure. `receiveFile` already owns this terminal
        // (delete outputs + `onTerminal("cancelled")` -> `finish`) and rethrows only to
        // unwind; see receiveHost.ts's abort branch. `finish`'s once-guard already made
        // the outcome correct, but the log below still fired, and `logError` forwards to
        // Sentry (lib/log.ts) — so every user cancel was filed as a production error
        // (PWA-7) and billed against the 5K-events/mo quota. The `finally` still disposes.
        //
        // Deliberately narrower than the extension host's `activeReceives.has` guard
        // (offscreen/index.ts): that shape would ALSO swallow a throw from the
        // post-`onTerminal("complete")` finalize/downloads tail (receiveHost.ts), which is
        // a real failure — bytes verified but never handed to the browser.
        return;
      }
      // Audit fix (#818 Part 4 sweep): this catch spans createWebReceiverDeps (which throws a
      // literal "not_paired") AND the whole receiveFile loop (network, OPFS worker, AEAD, hash).
      // Every class collapsed into an opaque INTERNAL with the cause discarded — the exact shape
      // that made the extension's own receive bug (#818) invisible for a day. The user-facing
      // outcome is unchanged; only the diagnostic identity is added.
      logError("filetransfer", "receive_failed", ftErrorIdentity(err));
      finish("failed", "INTERNAL");
    } finally {
      dispose?.();
      // Unconditional cleanup: `receiveFile` has a THIRD outcome besides
      // complete/throw — a mid-download network drop now resolves WITHOUT calling
      // `onTerminal` (§7.3: leave "transferring", retry next scan). `finish()`
      // (the only other `activeReceives.delete` site) never runs on that path, so
      // without this the entry leaks and `beginTransfer` blocks the resume scan
      // from ever re-driving this transfer for the rest of the page session.
      // Mirrors the extension offscreen host's generic finally. A no-op after a
      // terminal (`finish` already deleted it).
      activeReceives.delete(transferId);
    }
  })();
}

/**
 * Exhaustiveness pin for `handleInboundReceiveCtrl` below. Never read at
 * runtime — the `satisfies` is the point: a 13th `FileCtrl["kind"]` becomes a
 * COMPILE ERROR here, so a new kind forces an explicit handle-or-drop decision
 * at this receiver instead of falling into the `if` chain's silent default.
 */
const _receiverCtrlRouting = {
  cancel: "handle", // abort the active receive loop
  error: "handle", // abort the active receive loop
  resume: "handle", // §8.3 "Active record" row (paused records only)
  offer: "drop", // handled by fileCtrl.ts before this relay
  offer_received: "drop", // sender-bound
  accept: "drop", // sender-bound
  decline: "drop", // sender-bound
  progress: "drop", // sender-bound
  pause: "drop", // nothing emits an inbound pause (receiver-pause cut)
  complete: "drop", // sender-bound
  p2p_signal: "drop", // web receive has no P2P attempt to signal into
  demote: "drop", // never legitimately arrives at a receiver (§2)
} satisfies Record<FileCtrl["kind"], "handle" | "drop">;

/** Forward an inbound cancel/error/pause/resume ctrl for a receive (from `fileCtrl.ts`'s registered handler). */
export function handleInboundReceiveCtrl(ctrl: FileCtrl): void {
  if (ctrl.kind === "cancel" || ctrl.kind === "error") {
    const state = activeReceives.get(ctrl.transferId);
    if (state) state.aborted = true;
    return;
  }
  if (ctrl.kind === "resume") {
    void handleInboundResumeCtrl(ctrl.transferId);
  }
}

async function handleInboundResumeCtrl(transferId: string): Promise<void> {
  try {
    const record = await getReceiverState(transferId);
    if (record?.status !== "paused") return;
    const deps = await createWebReceiveResumeDeps();
    await resumeReceiverTransfer(record, deps);
  } catch (err) {
    // Best-effort (the record stays paused and the peer can retry) — but this is a peer-initiated
    // resume (the phone's FGS-park→Resume gesture), and a user-initiated action must never fail
    // without a trace.
    logWarn("filetransfer", "resume_receive_failed", ftErrorIdentity(err));
  }
}

/** Local user Cancel click on an ACTIVE record — always LOCAL origin, so this also calls `DELETE /v1/transfers/:id`. */
export async function cancelActiveReceive(transferId: string): Promise<void> {
  try {
    const record = await getReceiverState(transferId);
    if (!record) return;
    const deps = await createWebReceiveResumeDeps();
    const state = activeReceives.get(transferId);
    if (state) {
      await deps
        .sendCtrl(record.peerDeviceId, { kind: "cancel", transferId, reason: "user" })
        .catch(() => {});
      await deps.api.deleteTransfer(transferId).catch(() => {});
      state.aborted = true;
      return;
    }
  } catch (err) {
    // Same rule as the Resume gesture above — user-initiated, so never silent.
    logWarn("filetransfer", "cancel_receive_failed", ftErrorIdentity(err));
  }
}

/** The app-open resume trigger (§6.1's "receiver: on app open"). Best-effort. */
export async function resumePendingReceivesOnAppOpen(): Promise<void> {
  try {
    await pruneReceiverStates();
    const deps = await createWebReceiveResumeDeps();
    await resumePendingReceives(deps);
  } catch {
    // Best-effort — see doc above.
  }
}

/**
 * Startup GC (§3.1.4): enumerate the OPFS `ft/` directory and delete any
 * `send-*`/`recv-*` file whose transferId has no ACTIVE
 * (`"transferring"`/`"paused"`) record — orphans left by crashes in
 * pre-persist windows.
 */
export async function gcOrphanTransfers(): Promise<void> {
  try {
    const activeIds = new Set<string>();
    for (const rec of await listReceiverStates()) {
      if (isActiveTransferStatus(rec.status)) activeIds.add(rec.transferId);
    }
    try {
      for (const rec of await listSenderStates()) {
        if (isActiveTransferStatus(rec.status)) activeIds.add(rec.transferId);
      }
    } catch {
      // Sender store unreadable — GC receiver orphans only.
    }

    const root = await navigator.storage.getDirectory();
    const dir = await root.getDirectoryHandle("ft", { create: false }).catch(() => null);
    if (!dir) return;

    const orphans: string[] = [];
    for await (const name of dir.keys()) {
      const id = name.startsWith("recv-")
        ? name.slice("recv-".length)
        : name.startsWith("send-")
          ? name.slice("send-".length)
          : null;
      if (id !== null && !activeIds.has(id)) orphans.push(name);
    }
    for (const name of orphans) {
      await dir.removeEntry(name).catch(() => {});
    }
  } catch {
    // No ft/ directory, or enumeration failed — nothing more to do.
  }
}
